import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type {
  ActivityInstance,
  ActivityStatus,
  DiagramElement,
  Problem,
  TaskRequest,
} from '../api/types';
import { BpmnViewerLazy, type BpmnElementState } from '../bpmn/BpmnViewerLazy';
import { parseBpmnDefinitions } from '../bpmn/model';
import { buildProvenanceIndex } from '../bpmn/provenance';
import type { BpmnDefinitionsModel } from '../bpmn/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { KeyValue } from '../components/KeyValue';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Tabs } from '../components/Tabs';
import { StandardForm } from '../forms/StandardForm';
import { parseManifest } from '../microapp/manifest';
import { Link } from '../app/Link';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync } from '../app/useAsync';
import { usePlatformEvents } from '../app/usePlatformEvents';
import { activityStatusView, runOutcomeLabel, runStatusView, taskStatusView } from '../app/status';
import type { DetailPanel, Route, RunRoute } from '../app/router';
import { ElementDetail } from './ElementDetail';
import { ElementPicker } from './ElementPicker';
import { ProvenancePanel } from './ProvenancePanel';
import { formatDateTime } from '../lib/datetime';

export interface RunPageProps {
  route: RunRoute;
  navigate(route: Route, options?: { replace?: boolean }): void;
}

/**
 * 実行詳細。HumanTaskへの唯一の入口であり、独立したInboxや回答待ち一覧は作らない。
 * 図には現在位置だけでなく完了・失敗・取消・未訪問も重ね、並列の複数現在位置を全部出す。
 */
export function RunPage({ route, navigate }: RunPageProps) {
  const { api } = usePlatform();
  const { workspaceId, appId, runId } = route;

  const run = useAsync(() => api.getRun(workspaceId, runId), [api, workspaceId, runId]);
  const diagram = useAsync(() => api.getRunDiagram(workspaceId, runId), [api, workspaceId, runId]);
  const definition = useAsync(
    () => api.getDefinition(workspaceId, appId),
    [api, workspaceId, appId],
  );

  const [model, setModel] = useState<BpmnDefinitionsModel | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const bpmnXml = diagram.data?.bpmnXml ?? null;

  useEffect(() => {
    if (!bpmnXml) return;
    let active = true;
    setModel(null);
    setParseError(null);
    setRenderError(null);
    parseBpmnDefinitions(bpmnXml)
      .then((parsed) => {
        if (active) setModel(parsed);
      })
      .catch((error: unknown) => {
        if (active) setParseError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      active = false;
    };
  }, [bpmnXml]);

  const [taskReloadToken, setTaskReloadToken] = useState(0);
  const onEvent = useCallback(() => {
    // 状態と識別子しか届かないので、図とrunと選択中のTaskをsnapshotから取り直す。
    run.reload();
    diagram.reload();
    setTaskReloadToken((value) => value + 1);
  }, [run, diagram]);

  // 実行詳細はsnapshotとの取りこぼしを避けたいので、cursorが取れてから購読する。
  const eventCursor = diagram.data?.eventCursor ?? run.data?.eventCursor ?? null;
  const streamState = usePlatformEvents({
    api,
    workspaceId,
    filter: { runId },
    cursor: eventCursor,
    enabled: eventCursor !== null,
    onEvent,
  });

  // 期限切れcursorは再接続では回復しないので、snapshotを取り直して購読し直す。
  useEffect(() => {
    if (streamState === 'expired') {
      run.reload();
      diagram.reload();
    }
    // reloadの参照は毎回変わるため依存へ入れない。expiredの遷移だけで起動する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  const provenance = useMemo(() => (model ? buildProvenanceIndex(model) : null), [model]);
  const manifest = parseManifest(definition.data?.manifest ?? null, definition.data?.declarations ?? null);
  const elements = diagram.data?.elements ?? [];
  const selected = route.element ? elements.find((entry) => entry.activityId === route.element) : undefined;
  const instance = pickInstance(selected, route.instance ?? null);

  const setRoute = (patch: Partial<RunRoute>) =>
    navigate({ ...route, ...patch }, { replace: true });

  if (run.problem) {
    return (
      <div className="page">
        <ProblemNotice problem={run.problem} action={<Button onClick={run.reload}>再取得</Button>} />
      </div>
    );
  }

  // W/runs/{id} はWorkspace単位なので、URLのappと一致するかをShellで確かめる。
  if (run.data && run.data.appId !== appId) {
    return (
      <div className="page">
        <p className="failure">
          この実行はURLのmicroappに属していません（実際のappId: <code>{run.data.appId}</code>）。
        </p>
        <Link
          to={{ name: 'run', workspaceId, appId: run.data.appId, runId }}
          onNavigate={navigate}
        >
          正しいmicroappの実行詳細を開く
        </Link>
      </div>
    );
  }

  return (
    <div className="page run-page">
      <header className="app-heading">
        <div>
          <Link to={{ name: 'app', workspaceId, appId, tab: 'runs' }} onNavigate={navigate}>
            ← 実行履歴
          </Link>
          <h1>
            <code>{runId}</code>
          </h1>
          {run.data && (
            <KeyValue
              items={[
                {
                  label: '状態',
                  value: (
                    <Badge tone={runStatusView(run.data.status).tone}>
                      {runStatusView(run.data.status).label}
                    </Badge>
                  ),
                },
                { label: '終了', value: runOutcomeLabel(run.data) },
                { label: 'プロセス', value: <code>{run.data.processKey}</code> },
                { label: '起動', value: run.data.trigger },
                { label: '開始', value: formatDateTime(run.data.createdAt) },
                { label: '更新', value: formatDateTime(run.data.updatedAt) },
                ...(run.data.parentRunId
                  ? [{ label: '親run', value: <code>{run.data.parentRunId}</code> }]
                  : []),
              ]}
            />
          )}
          {run.data && runStatusView(run.data.status).hint && (
            <p className="hint">{runStatusView(run.data.status).hint}</p>
          )}
        </div>
        <span className="stream-state" data-testid="stream-state">
          通知: {streamState}
        </span>
      </header>

      <ProblemNotice problem={run.data?.error ?? null} />
      <ProblemNotice problem={diagram.problem} />
      {diagram.loading && <Spinner label="実行図を取得しています" />}
      {parseError && <p className="failure">実行時BPMNを解析できませんでした: {parseError}</p>}
      {renderError && <p className="failure">実行図を描画できませんでした: {renderError}</p>}

      <div className="diagram-layout">
        <div className="diagram-area">
          {model && !model.hasDiagram && (
            <div className="no-diagram">
              <p className="notice">
                この実行定義にはBPMNDIがありません。図は描けないため、要素を一覧から選びます。
              </p>
              {model.processes[0] && (
                <ElementPicker
                  process={model.processes[0]}
                  selectedElementId={route.element ?? null}
                  onSelect={(elementId) => setRoute({ element: elementId, instance: undefined })}
                  stateOf={(elementId) => {
                    const element = elements.find((entry) => entry.activityId === elementId);
                    if (!element) return null;
                    const view = activityStatusView(
                      (element.activeInstances[0] ?? element.lastInstance)?.status ?? 'queued',
                    );
                    return element.visitCount === 0
                      ? { tone: 'neutral', label: '未実行' }
                      : { tone: view.tone, label: view.label };
                  }}
                />
              )}
            </div>
          )}
          {bpmnXml && model?.hasDiagram !== false && (
            <Suspense fallback={<Spinner label="図を読み込んでいます" />}>
              <BpmnViewerLazy
                xml={bpmnXml}
                states={elements.map(toElementState)}
                selectedElementId={route.element ?? null}
                onSelect={(elementId) =>
                  setRoute({ element: elementId ?? undefined, instance: undefined })
                }
                onImportError={(error) => setRenderError(error.message)}
              />
            </Suspense>
          )}
        </div>

        <aside className="inspector">
          <Tabs
            label="要素の詳細"
            active={route.panel ?? 'detail'}
            onSelect={(key) => setRoute({ panel: key as DetailPanel })}
            items={[
              { key: 'detail', label: '詳細' },
              { key: 'answer', label: '回答' },
              { key: 'source', label: '出典' },
            ]}
          />

          {!route.element && <p className="hint">図の要素を選ぶと状態と実行回が表示されます。</p>}

          {route.element && selected && (
            <InstanceSelector
              element={selected}
              selectedInstanceId={instance?.activityInstanceId ?? null}
              onSelect={(activityInstanceId) => setRoute({ instance: activityInstanceId })}
            />
          )}

          {route.panel === 'source' && provenance && (
            <ProvenancePanel
              index={provenance}
              elementId={route.element ?? null}
              onSelectElement={(elementId) => setRoute({ element: elementId, instance: undefined })}
            />
          )}

          {route.panel !== 'source' && route.element && model && (
            <>
              {/* 図と出典はrunの実行定義、manifestは現在の定義。出所が違うことを隠さない。 */}
              <p className="notice">
                図と出典はこのrunの実行定義、実装の対応は現在の定義のmanifestです。
              </p>
              <ProcessElementDetail
                model={model}
                processKey={diagram.data?.processKey ?? ''}
                elementId={route.element}
                manifest={manifest}
                onOpenDecision={(decision) =>
                  navigate({ name: 'app', workspaceId, appId, tab: 'decisions', decision })
                }
                onOpenProcess={(processKey) =>
                  navigate({ name: 'app', workspaceId, appId, tab: 'process', process: processKey })
                }
              />
            </>
          )}

          {instance?.childRunId && (
            <p className="row">
              <span className="field-label">Call Activity</span>
              <Link
                to={{ name: 'run', workspaceId, appId, runId: instance.childRunId }}
                onNavigate={navigate}
              >
                子runを開く
              </Link>
            </p>
          )}

          {instance && (
            <TaskPanel
              workspaceId={workspaceId}
              appId={appId}
              instance={instance}
              reloadToken={taskReloadToken}
              showAnswerForm={route.panel === 'answer'}
              onOpenAnswer={() => setRoute({ panel: 'answer' })}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

function ProcessElementDetail(props: {
  model: BpmnDefinitionsModel;
  processKey: string;
  elementId: string;
  manifest: ReturnType<typeof parseManifest>;
  onOpenDecision(decisionKey: string): void;
  onOpenProcess(processKey: string): void;
}) {
  const process =
    props.model.processes.find((entry) => entry.processKey === props.processKey) ??
    props.model.processes[0];
  if (!process) return null;
  return (
    <ElementDetail
      process={process}
      elementId={props.elementId}
      manifest={props.manifest}
      onOpenDecision={props.onOpenDecision}
      onOpenProcess={props.onOpenProcess}
    />
  );
}

/** ループ/MIで同じactivityIdが複数回実行されるため、実行回を明示的に選ばせる。 */
function InstanceSelector({
  element,
  selectedInstanceId,
  onSelect,
}: {
  element: DiagramElement;
  selectedInstanceId: string | null;
  onSelect(activityInstanceId: string): void;
}) {
  const instances = [
    ...element.activeInstances.map((entry) => ({ entry, current: true })),
    ...(element.lastInstance &&
    !element.activeInstances.some(
      (active) => active.activityInstanceId === element.lastInstance?.activityInstanceId,
    )
      ? [{ entry: element.lastInstance, current: false }]
      : []),
  ];

  return (
    <section className="detail-section">
      <KeyValue
        items={[
          { label: 'activityId', value: <code>{element.activityId}</code> },
          { label: '実行回数', value: String(element.visitCount) },
          { label: '現在の実行回', value: String(element.activeInstances.length) },
        ]}
      />
      {instances.length === 0 ? (
        <p className="hint">この要素はまだ実行されていません。</p>
      ) : (
        <ul className="instance-list">
          {instances.map(({ entry, current }) => {
            const view = activityStatusView(entry.status);
            return (
              <li key={entry.activityInstanceId}>
                <Button
                  variant={entry.activityInstanceId === selectedInstanceId ? 'primary' : 'default'}
                  onClick={() => onSelect(entry.activityInstanceId)}
                >
                  <Badge tone={view.tone}>{view.label}</Badge>
                  <code>{entry.activityInstanceId}</code>
                  {current ? '（現在）' : '（最後に終了）'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TaskPanel({
  workspaceId,
  appId,
  instance,
  reloadToken,
  showAnswerForm,
  onOpenAnswer,
}: {
  workspaceId: string;
  appId: string;
  instance: ActivityInstance;
  reloadToken: number;
  showAnswerForm: boolean;
  onOpenAnswer(): void;
}) {
  const { api } = usePlatform();
  const requestId = instance.requestId;
  const task = useAsync(
    () => (requestId ? api.getTask(workspaceId, requestId) : Promise.resolve(null)),
    [api, workspaceId, requestId, reloadToken],
  );
  const [submitting, setSubmitting] = useState(false);
  const [answerProblem, setAnswerProblem] = useState<Problem | null>(null);

  if (!requestId) {
    return (
      <p className="hint">
        この実行回にはまだTask参照がありません。通知を受けたら自動で取り直します。
      </p>
    );
  }
  if (task.loading) return <Spinner label="Taskを取得しています" />;
  if (task.problem) return <ProblemNotice problem={task.problem} />;
  if (!task.data) return null;

  const request: TaskRequest = task.data;
  // frameに固定したappと同じかをShellでも確かめる。
  if (request.appId !== appId) {
    return <p className="failure">このTaskはURLのmicroappに属していません。</p>;
  }

  const status = taskStatusView(request.status);
  const submit = async (answer: Record<string, unknown>) => {
    setSubmitting(true);
    setAnswerProblem(null);
    try {
      await api.answerTask(
        workspaceId,
        request.id,
        { expectedVersion: request.version, answer },
        { idempotencyKey: newIdempotencyKey() },
      );
      task.reload();
    } catch (error) {
      setAnswerProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title={`Task ${request.taskKey}`}>
      <KeyValue
        items={[
          { label: '状態', value: <Badge tone={status.tone}>{status.label}</Badge> },
          { label: 'requestId', value: <code>{request.id}</code> },
          { label: 'version', value: String(request.version) },
          { label: '種別', value: request.kind === 'human' ? 'HumanTask' : 'ServiceTask' },
        ]}
      />
      {status.hint && <p className="hint">{status.hint}</p>}
      <ProblemNotice problem={request.error} />

      <details>
        <summary>公開input</summary>
        <pre>{JSON.stringify(request.input, null, 2)}</pre>
      </details>
      {request.output && (
        <details>
          <summary>公開output</summary>
          <pre>{JSON.stringify(request.output, null, 2)}</pre>
        </details>
      )}

      {request.humanRequest && !showAnswerForm && (
        <Button onClick={onOpenAnswer}>回答フォームを開く</Button>
      )}

      {request.humanRequest && showAnswerForm && (
        <StandardForm
          request={request.humanRequest}
          expectedVersion={request.version}
          canAnswer={request.status === 'awaiting_answer'}
          submitting={submitting}
          problem={answerProblem}
          onSubmit={(answer) => void submit(answer)}
          onReload={task.reload}
        />
      )}
    </Card>
  );
}

/** 図に重ねる状態。並列は現在位置を全部出し、最後の1件を現在と決めつけない。 */
function toElementState(element: DiagramElement): BpmnElementState {
  // 現在の実行回があればそれを図に出す。回答待ちは完了とも失敗とも別の色で示す。
  const instance = element.activeInstances[0] ?? element.lastInstance;
  if (!instance) {
    return { activityId: element.activityId, status: 'unvisited', visitCount: 0 };
  }
  return {
    activityId: element.activityId,
    status: diagramStatusToViewerStatus(instance.status),
    visitCount: element.visitCount,
  };
}

function diagramStatusToViewerStatus(status: ActivityStatus): BpmnElementState['status'] {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    case 'awaiting_answer':
      return 'awaiting';
    case 'outcome_unknown':
      return 'unknown';
    default:
      // active / queued / running / preparing / processing_answer はまとめて進行中に寄せる。
      return 'active';
  }
}

function pickInstance(
  element: DiagramElement | undefined,
  activityInstanceId: string | null,
): ActivityInstance | null {
  if (!element) return null;
  const candidates = [...element.activeInstances, ...(element.lastInstance ? [element.lastInstance] : [])];
  if (activityInstanceId) {
    return candidates.find((entry) => entry.activityInstanceId === activityInstanceId) ?? null;
  }
  return element.activeInstances[0] ?? element.lastInstance ?? null;
}
